import { useMemo } from 'react';
import { Box, Card } from '@nimbus-ds/components';
import { PaymentFeeDto, TaxDto } from '@tiendanube/common';
import { CheckoutPaymentMethod } from '@tiendanube/common/src/enums';
import {
  useChargesToPay,
  usePayorderToPay,
} from 'domains/Billing/Checkout/hooks';
import {
  AccountDataContent,
  ChargesSelectionSummary,
  PaymentDataContent,
} from 'domains/Billing/Checkout/pages/components';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { ChargeDisplayInterface } from '../PaymentDataContent/PaymentDataContent';

interface PayOrderSummaryProps {
  showTaxes?: boolean;
  showPeriodDiscounts?: boolean;
  paymentMethod?: CheckoutPaymentMethod;
}

function transformExtraFeesToChargeDisplay(
  description: string,
  fees?: PaymentFeeDto[],
): [ChargeDisplayInterface | undefined, TaxDto[]] {
  if (!fees?.length) return [undefined, []];
  const charges: ChargeDisplayInterface = {
    amountValue: 0,
    amountCurrency: fees[0].amountCurrency,
    conceptCode: fees[0].concept.code,
    description,
  };
  let taxes: TaxDto[] = [];

  fees.forEach((fee) => {
    taxes = taxes.concat(fee.taxes);
    charges.amountValue += fee.amountValue;
  });

  return [charges, taxes];
}

function PayOrderSummary({
  showTaxes = false,
  showPeriodDiscounts = false,
  paymentMethod,
}: Readonly<PayOrderSummaryProps>) {
  const { transformChargesToDisplayable } = useChargesToPay(false);
  const {
    payorderToPay,
    isSuccess,
    totalWithoutFees,
    subtotalWithoutFees,
    totalFeeForMethod,
    nonFeeCharges,
    feesForMethod,
  } = usePayorderToPay();
  const t = useTranslationBilling('checkout.payOrderSummary');

  const feeTotal = (paymentMethod && totalFeeForMethod(paymentMethod)) ?? 0;

  const charges = useMemo(
    () => nonFeeCharges && transformChargesToDisplayable(nonFeeCharges),
    [nonFeeCharges, transformChargesToDisplayable],
  );

  if (!isSuccess || !payorderToPay || !totalWithoutFees || !subtotalWithoutFees)
    return null;

  const [extraFeeCharge, extraFeeTaxes] = transformExtraFeesToChargeDisplay(
    t('extraFeeDescription'),
    paymentMethod && feesForMethod(paymentMethod),
  );

  return (
    <Card padding="none">
      <Card.Header padding="base" title={t('total')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" flexGrow="1">
          <Box padding="4">
            <AccountDataContent />
          </Box>
          <hr />
          <Box padding="4">
            {charges && (
              <PaymentDataContent
                showPeriodDiscounts={showPeriodDiscounts}
                charges={[
                  ...charges,
                  ...(extraFeeCharge ? [extraFeeCharge] : []),
                ]}
              />
            )}
          </Box>
          <hr />
          <Box padding="4" backgroundColor="neutral-surface">
            {charges && (
              <ChargesSelectionSummary
                charges={charges}
                showTaxes={showTaxes}
                showPeriodDiscounts={showPeriodDiscounts}
                extraFee={feeTotal}
                extraTaxes={extraFeeTaxes}
              />
            )}
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

export default PayOrderSummary;
